import { Fragment, useMemo } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CommissionPolicyForm } from '@/pages/commission-policy/components/CommissionPolicyForm';
import { useGetGlobalCommissionPolicyQuery } from '@/services/CommissionPolicyService';
import { usePermissions } from '@/hooks/usePermissions';

const CommissionPolicyConfiguration = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { hasPermission } = usePermissions();
  const canRead = hasPermission('commission_policy_management_read');
  const canUpdate = hasPermission('commission_policy_management_update');

  const modeParam = searchParams.get('mode');
  const mode = (
    modeParam === 'edit' || modeParam === 'create' || modeParam === 'view'
      ? modeParam
      : 'view'
  ) as 'view' | 'edit' | 'create';

  const { data: policy, isLoading } = useGetGlobalCommissionPolicyQuery();
  const normalizedPolicy = policy ?? null;

  const globalMode = useMemo(() => {
    if (!normalizedPolicy) return 'create';
    return mode;
  }, [mode, normalizedPolicy]);

  const readOnlyReason = useMemo(() => {
    if (isLoading) {
      return 'Loading commission policy...';
    }

    if (!canRead) {
      return "You don't have permission to view commission policies.";
    }
    if (globalMode !== 'view' && !canUpdate) {
      return "You don't have permission to update commission policies.";
    }
    return undefined;
  }, [canRead, canUpdate, globalMode, isLoading]);

  const handleDone = () => {
    navigate('/configuration/commission');
  };

  const handleEdit = () => {
    navigate('/configuration/commission?mode=edit');
  };

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle id="commission-policy-configuration-heading" />
              <ToolbarDescription>
                Configure global default commission policy.
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              {normalizedPolicy && canUpdate && globalMode === 'view' && (
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleEdit}
                >
                  Edit
                </button>
              )}
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}

      <Container>
        <CommissionPolicyForm
          mode={globalMode}
          policy={normalizedPolicy}
          onDone={handleDone}
          readOnlyReason={readOnlyReason}
        />
      </Container>
    </Fragment>
  );
};

export { CommissionPolicyConfiguration };
